import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories, siteInfo } from '@/mocks/articles';
import { generateWebPageSchema, generateBreadcrumbSchema } from '@/components/feature/StructuredData';

const categoryNameMap: Record<string, string> = {
  celebrity: '明星黑料',
  influencer: '网红内幕',
  kol: '大V爆料',
  society: '社会热点',
  entertainment: '娱乐圈',
};

const categoryDescMap: Record<string, string> = {
  celebrity: '最新明星八卦爆料，明星私生活、恋情曝光、离婚内幕等明星黑料第一时间发布。',
  influencer: '网红圈最新内幕，知名网红翻车事件、直播带货黑幕等网红爆料合集。',
  kol: '大V最新动态曝光，意见领袖虚假宣传、骗局揭露等大V爆料内容。',
  society: '社会热点事件深度曝光，民生问题、行业黑幕调查等社会热点资讯。',
  entertainment: '娱乐圈最新动态，综艺节目内幕、选秀暗箱操作等娱乐圈爆料。',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = slug === 'all' ? '全部' : (categoryNameMap[slug || ''] || slug);
  const categoryDesc = slug === 'all'
    ? '浏览黑料91全网最新黑料爆料内容，涵盖明星、网红、大V、社会热点、娱乐圈等多个分类。'
    : (categoryDescMap[slug || ''] || '最新黑料爆料内容');

  const filteredArticles = slug === 'all'
    ? articles
    : articles.filter((a) => a.category === slug);

  const pageTitle = `${categoryName} - 黑料91`;
  const canonicalUrl = `https://${siteInfo.domain}/category/${slug}`;

  const webPageLD = generateWebPageSchema(
    pageTitle,
    categoryDesc,
    canonicalUrl,
    [
      { name: '首页', url: `https://${siteInfo.domain}` },
      { name: categoryName, url: canonicalUrl },
    ]
  );

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={categoryDesc}
        keywords={`黑料91,黑料91${categoryName},${categoryName}爆料,黑料91最新`}
        canonicalUrl={canonicalUrl}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageLD) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <nav className="w-full px-4 md:px-6 py-3 text-sm text-foreground-500" aria-label="面包屑导航">
            <ol className="flex items-center gap-1.5 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li>/</li>
              <li className="text-foreground-950 font-medium">{categoryName}</li>
            </ol>
          </nav>

          <section className="w-full px-4 md:px-6 py-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {categoryDesc}
                </p>
              </div>
              <span className="text-sm text-foreground-400 whitespace-nowrap">
                共 {filteredArticles.length} 篇
              </span>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group cursor-pointer bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300/50 transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        {article.isHot && (
                          <span className="bg-primary-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-full">
                            热门
                          </span>
                        )}
                        {article.isTop && (
                          <span className="bg-accent-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-full">
                            置顶
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <div className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          <span>{(article.views / 10000).toFixed(1)}万</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}